import type { CardCharge } from '../api/client'
import type { SavedCard } from '../types/app'

const BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
}

const WALLETS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
}

function brandName(brand: string): string {
  return BRANDS[brand.toLowerCase()] ?? (brand ? brand[0].toUpperCase() + brand.slice(1) : 'Card')
}

// cardLabel is how a saved card reads on a button or row: "Visa •••• 4242",
// with the wallet it came from when there is one.
export function cardLabel(card: Pick<SavedCard, 'brand' | 'last4' | 'wallet'>): string {
  const wallet = card.wallet ? WALLETS[card.wallet] ?? card.wallet : ''
  return `${brandName(card.brand)} •••• ${card.last4}${wallet ? ` (${wallet})` : ''}`
}

// cardExpiry is "04/27".
export function cardExpiry(card: Pick<SavedCard, 'exp_month' | 'exp_year'>): string {
  return `${String(card.exp_month).padStart(2, '0')}/${String(card.exp_year % 100).padStart(2, '0')}`
}

// usableCard is whether a client's card may be offered: there is one
// and it has not expired.
export function usableCard(card: SavedCard | null | undefined): card is SavedCard {
  return !!card && !card.expired
}

export type ChargeResult =
  | { kind: 'checking' }
  | { kind: 'paid' }
  | { kind: 'needs_client' }
  | { kind: 'declined'; heading: string }

// chargeResult maps what the server says about a charge to what the sheet shows.
export function chargeResult(c: CardCharge): ChargeResult {
  if (c.status === 'pending') return { kind: 'checking' }
  if (c.status === 'succeeded') return { kind: 'paid' }
  if (c.status === 'needs_client' || c.failure === 'needs_client') return { kind: 'needs_client' }
  if (c.failure === 'no_answer') return { kind: 'declined', heading: 'We couldn’t reach the card' }
  return { kind: 'declined', heading: 'Card declined' }
}
